import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import type {TikTokPrivacySettings} from '@Components/social-media/post.vo';
import {getReadableTikTokPrivacySetting, TikTokPrivateAccountPrivacySettings, TikTokPublicAccountPrivacySettings} from '@Components/social-media/post.vo';
import type {ListItemStorage} from '@Components/list/list.types';
import {LIST_ITEM_TYPE} from '@Components/list/list.types';
import {updateTikTokPublishingParamPrivacySettings} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {ALIGNMENT_TYPE, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {List} from '@Components/list';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Dropdown} from '@Components/dropdown-v2';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {noop} from '@Utils/general.util';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {usePostPublishingParamsForAccount} from '@Components/social-media/social-media.hooks';
import styles from './tiktok-privacy-selector.module.scss';
import {useAppDispatch} from '@/hooks';
import {clearList} from '@Components/list/list-reducer';

interface TiktokPrivacySelectorProps {
  account: SocialAccount;
  onSelectOption?: (selectedOption: TikTokPrivacySettings) => void;
  isLoading?: boolean;
}

export function TiktokPrivacySelector({account, onSelectOption = noop, isLoading = false}: TiktokPrivacySelectorProps): ReactElement<any> | null {
  const [selectedPrivacySettings, setSelectedPrivacySettings] = useState<TikTokPrivacySettings>(TikTokPublicAccountPrivacySettings.MUTUAL_FOLLOW_FRIENDS);
  const [dropdownLabel, setDropdownLabel] = useState(getReadableTikTokPrivacySetting(selectedPrivacySettings));
  const publishingParamsForAccount = usePostPublishingParamsForAccount(account.id);
  const dispatch = useAppDispatch();
  const listId = `tiktok-account-sort-dropdown-list-${account.id}`;

  const getTikTokSpecificId = (privacySetting: TikTokPrivacySettings): string => {
    return `tiktok-${account.id}-${privacySetting}`;
  };

  const getListItemByPrivacySetting = (privacySetting: TikTokPrivacySettings, isUnmargined = false): ListItemStorage => {
    return {
      id: getTikTokSpecificId(privacySetting),
      isSelected: selectedPrivacySettings === privacySetting,
      type: LIST_ITEM_TYPE.DEFAULT_3,
      isItemUnmargined: isUnmargined,
      text: getReadableTikTokPrivacySetting(privacySetting),
      onClick(_listId?: string, listItemId?: string): void {
        onSelectOption(privacySetting);
        if (listItemId === getTikTokSpecificId(privacySetting)) {
          setDropdownLabel(getReadableTikTokPrivacySetting(privacySetting));
          dispatch(
            updateTikTokPublishingParamPrivacySettings({
              idSocialMediaAccount: account.id,
              privacySettings: privacySetting,
            })
          );
        }
      },
    };
  };

  const privateAccountListItems: ListItemStorage[] = [
    getListItemByPrivacySetting(TikTokPrivateAccountPrivacySettings.FOLLOWER_OF_CREATOR),
    getListItemByPrivacySetting(TikTokPrivateAccountPrivacySettings.MUTUAL_FOLLOW_FRIENDS),
    getListItemByPrivacySetting(TikTokPrivateAccountPrivacySettings.SELF_ONLY, true),
  ];

  const publicAccountListItems: ListItemStorage[] = [
    getListItemByPrivacySetting(TikTokPublicAccountPrivacySettings.PUBLIC_TO_EVERYONE),
    getListItemByPrivacySetting(TikTokPublicAccountPrivacySettings.MUTUAL_FOLLOW_FRIENDS),
    getListItemByPrivacySetting(TikTokPublicAccountPrivacySettings.SELF_ONLY, true),
  ];

  const getPrivacySelectorButton = (): ReactElement<any> => {
    return <DropdownDefaultSelectBox id={`tiktok-privacy-settings-selector-${account.id}`} size={DROPDOWN_SIZE.SMALL} text={dropdownLabel} className="_unmargin" />;
  };

  const getListItemsByAccountType = (): ListItemStorage[] => {
    return account.tikTokMeta?.isPublicAccount ? publicAccountListItems : privateAccountListItems;
  };

  const getPrivacySettingsList = (): ReactElement<any> => {
    return <List id={listId} className="spacing-m-0" draggable={false} multiSelect={false} items={getListItemsByAccountType()} keepAtleastOneItemSelected />;
  };

  const getPrivacyOptions = (): ReactElement<any> => {
    if (isLoading) {
      return <SkeletonBrick width={60} height={30} className="spacing-m-r-4 radius-4" />;
    }

    return (
      <Dropdown
        id={`tiktok-social-posts-sort-${account.id}`}
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        selector={getPrivacySelectorButton()}
        popup={getPrivacySettingsList()}
        popupClassName={styles.dropdown}
        popUpHasCustomWidth
      />
    );
  };

  useEffect(() => {
    if (!publishingParamsForAccount) {
      return;
    }

    if ('privacySettings' in publishingParamsForAccount) {
      const existingPrivacySetting = publishingParamsForAccount.privacySettings as TikTokPrivacySettings;
      setDropdownLabel(getReadableTikTokPrivacySetting(existingPrivacySetting));
      setSelectedPrivacySettings(existingPrivacySetting);
    }

    return (): void => {
      dispatch(clearList({id: listId}));
    };
  }, []);

  return (
    <div className="flex-h-row flex-content-between">
      <div className="flex-center">
        <Icon icon="icon-globe" className="spacing-m-r-2" type={IconType.NONE} size={IconSize.SIZE_ICON_20} />
        <Text val={window.i18next.t('pmwjs_privacy')} size={TextSize.XSMALL} />
      </div>
      <div>{getPrivacyOptions()}</div>
    </div>
  );
}
